import { createRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toDisplayString, toSafeReactNode } from "@/lib/utils";
import { rootRoute } from "./__root";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-surface p-6 space-y-3">
      <h2 className="text-base font-semibold text-text">{toSafeReactNode(title)}</h2>
      <div className="text-sm text-text-muted leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function PrivacyPage() {
  const { t } = useTranslation("common");

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto max-w-2xl px-6 py-12 space-y-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-accent transition-colors">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          {toSafeReactNode(t("privacyPage.back"))}
        </Link>

        <div>
          <h1 className="text-3xl font-bold tracking-tight text-text">{toSafeReactNode(t("privacyPage.title"))}</h1>
          <p className="mt-2 text-sm text-text-muted">{toSafeReactNode(t("privacyPage.lastUpdated"))}</p>
        </div>

        <div className="space-y-6">
          <Section title={toDisplayString(t("privacyPage.section1Title"))}>
            <p>{toSafeReactNode(t("privacyPage.section1Content"))}</p>
            <p>
              <strong className="text-text">Lead-Capture:</strong> Die Extension erkennt auf besuchten Seiten
              öffentlich sichtbare Kontaktdaten (Name, E-Mail, Firma, Telefon) und überträgt diese
              auf Nutzeraktion an das verbundene CRM. Als Nutzer tragen Sie die datenschutzrechtliche
              Verantwortung für erfasste Drittdaten.
            </p>
            <p>
              <strong className="text-text">Zahlungsabwicklung:</strong> Über Stripe (keine Kreditkartendaten
              bei uns gespeichert). Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
            </p>
            <p>
              <strong className="text-text">Nutzungsanalyse (PostHog):</strong> Nur mit Ihrer Einwilligung.
              Widerruf jederzeit unter Einstellungen → Datenschutz möglich.
              Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO.
            </p>
            {/*
              Beschreibt die serverseitigen Schreibstellen in `analytics_events`, die die
              Analyse-Einwilligung NICHT lesen und die Konto-ID immer mitschreiben
              (einwilligungsabhängig ist nur der Client-Ingest, routes/analytics.ts):
                Vertragsereignisse     capture.ts (first_capture_succeeded),
                                       crm.ts (crm_connected, beide Verbindungspfade),
                                       capture.ts (paywall_shown),
                                       billing.ts (subscription_started, referral_paid_active,
                                       subscription_cancelled),
                                       referrals.ts (referral_signup)
                Kernfunktion           capture.ts (capture_attempted),
                                       capture-outcome.ts (capture_succeeded, capture_failed)
                Fehlerklassen          capture-outcome.ts (stage, error_class),
                                       crm.ts (reason_class)
                Funktionsnutzung       lead-signals.ts (lead_signals_viewed,
                                       company_graph_viewed), reminders.ts (reminder_created),
                                       team.ts (team_member_invited)
                Extension-Installation install-telemetry.ts (install_completed) über requireAuth
              Ohne Zeilennummern, absichtlich: die vorherige Fassung nannte capture.ts:224 und
              team.ts:377 — beide waren schon vor dieser Phase falsch, weil Zeilennummern in
              Prosa lautlos verrutschen. Der Ereignisname verrutscht nicht, und er ist das,
              was hier rechtlich zählt.
              ALLE diese Stellen schreiben über emitAnalytics (apps/api/src/telemetry.ts) —
              erzwungen von analytics-single-writer.test.ts, das jede direkte
              `.from("analytics_events")`-Schreibanweisung außerhalb dieses Moduls ablehnt.
              Damit gilt der Freitext-Ausschluss (telemetry.ts findPiiViolation) auch für den
              unauthentifizierten Client-Ingest, der ihn vorher als Einziger nicht anwandte —
              dort mit 400 `validation_error` statt einer stillen Verwerfung.
              Der Filter prüft normalisierte Schlüssel (leadEmail/lead-email/lead_email treffen
              dieselbe Regel), URLs auch ohne Schema, Telefonnummern, die Schlüssel selbst und
              Nicht-String-Werte.
              Die PostHog-Ausnahme: beide Abo-Ereignisse in billing.ts werden zusätzlich
              weitergeleitet, aber nur bei Einwilligung.
              Bewusst ohne Angabe einer Rechtsgrundlage und ohne Aufbewahrungsfrist — beides
              steht auf der Anwaltsliste in docs/ops/10_launch_checklist.md §C1.
              Wortgleich mit apps/landing/src/pages/privacy.astro §7.
            */}
            <p>
              <strong className="text-text">Betriebliche Ereignisprotokolle:</strong> Unabhängig von
              der Analyse-Einwilligung protokolliert TakeLead in der eigenen Datenbank Ereignisse
              zur Nutzung des Dienstes mit Bezug zur internen Konto-ID. Protokolliert werden:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong className="text-text">Vertragsereignisse:</strong> erste erfolgreiche
                Erfassung, Verbindung eines CRM, Anzeige einer Upgrade-Aufforderung, Start und
                Kündigung eines Abonnements, Empfehlungen.
              </li>
              <li>
                <strong className="text-text">Nutzung der Kernfunktion:</strong> jeder
                Erfassungsvorgang — Versuch, Erfolg und Fehlschlag — jeweils mit dem verwendeten
                CRM-Anbieter. Dies ist ein Protokoll der laufenden Nutzung, nicht nur eine
                Zählung einzelner Meilensteine.
              </li>
              <li>
                <strong className="text-text">Fehlerklassen:</strong> bei fehlgeschlagenen
                Erfassungen und fehlgeschlagenen CRM-Verbindungen zusätzlich die
                Bearbeitungsstufe und eine klassifizierte Fehlerursache aus einem festen
                Vokabular. Der Fehlertext des CRM-Anbieters wird nicht gespeichert.
              </li>
              <li>
                <strong className="text-text">Funktionsnutzung:</strong> Aufruf von Intent Signals
                und Company Graph, Anlage einer Erinnerung, Versand einer Team-Einladung — jeweils
                mit dem Tarif des Kontos und, je nach Funktion, der Anzahl angezeigter Einträge
                oder der Angabe, ob eine Erinnerung zu einer Erfassung gehört.
              </li>
            </ul>
            <p>
              Zusätzlich wird festgehalten, dass eine TakeLead-Browser-Extension installiert ist
              und sich erstmals mit dem Konto verbunden hat. Die Extension selbst sendet keine
              Nutzungsdaten; dieses Ereignis wird serverseitig aus ihrer ersten Anfrage abgeleitet
              und bei späterer Nutzung nicht wiederholt.
            </p>
            <p>
              Diese Protokolle enthalten keine Daten der erfassten Kontaktpersonen und keine
              selbst eingegebenen Freitexte (etwa Titel oder Notiz einer Erinnerung) — nur
              Kennungen, Klassen, Zahlen und Ja/Nein-Angaben. Sie werden nicht an PostHog
              übermittelt; ausgenommen sind Start und Kündigung eines Abonnements, die zusätzlich
              an PostHog gehen, wenn eine Einwilligung in die Nutzungsanalyse vorliegt.
            </p>
          </Section>

          <Section title="3. Auftragsverarbeiter">
            <p>Wir nutzen folgende Dienste mit abgeschlossenem AVV gemäß Art. 28 DSGVO:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-text">Supabase</strong> – Datenbank, EU-Server (Frankfurt)</li>
              <li><strong className="text-text">Vercel</strong> – Hosting (USA, auf Basis von SCC)</li>
              <li><strong className="text-text">Stripe</strong> – Zahlungsabwicklung (USA, auf Basis von SCC)</li>
              <li><strong className="text-text">Resend</strong> – Transaktionale E-Mails</li>
            </ul>
          </Section>

          <Section title={toDisplayString(t("privacyPage.section2Title"))}>
            <p>{toSafeReactNode(t("privacyPage.section2Content"))}</p>
          </Section>

          <Section title={toDisplayString(t("privacyPage.section3Title"))}>
            <p>{toSafeReactNode(t("privacyPage.section3Content"))}</p>
          </Section>

          <Section title="5. Speicherdauer">
            <p>
              Nutzerdaten bleiben gespeichert, bis das Konto gelöscht wird. Eine Kündigung des
              Abonnements beendet das Abonnement, nicht das Konto — eine automatische Löschung
              nach Ablauf einer Frist findet nicht statt.
            </p>
            <p>
              Die Kontolöschung ist jederzeit im Dashboard unter Einstellungen → Support möglich.
              Dabei wird ein laufendes Stripe-Abonnement gekündigt und das Nutzerkonto gelöscht.
            </p>
            <p>
              <strong className="text-text">Was die Kontolöschung entfernt:</strong> das Profil,
              die Erfassungs-Metadaten, die CRM-Verbindungen samt gespeicherter Zugangstoken,
              den Abonnement-Datensatz, Empfehlungen und Affiliate-Daten, Erinnerungen,
              Intent-Signale und Firmengraph-Einträge, selbst angelegte Teams, Team-Mitgliedschaften
              und selbst versendete Einladungen, Kündigungsgründe — sowie die selbst eingegebenen
              Rückmeldungs- und Support-Freitexte.
            </p>
            <p>
              <strong className="text-text">Was bestehen bleibt — und was es nicht enthält:</strong>{" "}
              Zwei Arten von Einträgen überleben die Kontolöschung, beide ohne Bezug zum
              gelöschten Konto. <em>Sicherheitsrelevante Vorgangsprotokolle</em> (etwa: eine
              CRM-Verbindung wurde hergestellt, eine Einladung wurde ausgesprochen, ein Abonnement
              wurde gekündigt) bleiben als Nachweis des Vorgangs bestehen; bei der Löschung werden
              daraus die Kontokennung, eine etwaige IP-Adresse und sämtliche Freitext- und
              Adressangaben entfernt — zurück bleiben nur Einstufungswerte wie Tarifkennung,
              CRM-Anbieter und Zählwerte. <em>Betriebliche Ereignisprotokolle</em> (Abschnitt 1)
              verlieren ebenfalls den Kontobezug; sie enthalten bauartbedingt keine
              Kontaktpersonen-Daten, keine Adressen, keine Profil-URLs und keine Freitexte — ein
              Ereignis, das so etwas mitführt, wird vollständig abgewiesen statt gespeichert.
            </p>
            <p>
              <strong className="text-text">Keine Frist zugesagt.</strong> Für diese verbleibenden
              Einträge sieht die Datenbank eine Löschung nach 90 bzw. 365 Tagen vor. Ob der
              zugehörige geplante Datenbankauftrag tatsächlich läuft, ist bislang nicht überprüft
              — deshalb wird hier keine Aufbewahrungsfrist zugesagt, sondern nur beschrieben, was
              die Einträge nach der Kontolöschung noch enthalten.
            </p>
            <p>
              <strong className="text-text">Einladung einer Person ohne Konto:</strong> Wird jemand
              in ein Team eingeladen, wird die eingegebene E-Mail-Adresse gespeichert, bis die
              Einladung angenommen, das Team aufgelöst oder das einladende Konto gelöscht wird.
              Eine eingeladene Person kann diese Speicherung derzeit nicht selbst beenden; Anfragen
              nach Abschnitt 6 an{" "}
              <a href="mailto:privacy@takelead.app" className="text-accent hover:underline">
                privacy@takelead.app
              </a>.
            </p>
          </Section>

          <Section title={toDisplayString(t("privacyPage.section4Title"))}>
            <p>{toSafeReactNode(t("privacyPage.section4Content"))}</p>
          </Section>

          <Section title="6. Ihre Rechte">
            <p>Sie haben das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16),
              Löschung (Art. 17), Einschränkung (Art. 18), Datenübertragbarkeit (Art. 20)
              und Widerspruch (Art. 21 DSGVO).</p>
            <p>
              Anfragen an:{" "}
              <a href="mailto:privacy@takelead.app" className="text-accent hover:underline">
                privacy@takelead.app
              </a>
              {" "}· Antwort innerhalb von 30 Tagen.
            </p>
            <p>
              Beschwerderecht bei der zuständigen Datenschutzbehörde:{" "}
              <a href="https://www.bfdi.bund.de" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                bfdi.bund.de
              </a>
            </p>
          </Section>

          <Section title="7. Cookies">
            <p>
              Technisch notwendige Session-Cookies für die Authentifizierung (keine Einwilligung
              erforderlich nach § 25 Abs. 2 TTDSG). Analyse-Cookies nur mit Ihrer Einwilligung.
            </p>
          </Section>

          <Section title="8. Chrome Web Store – Limited Use">
            <p>Erklärung im von Google vorgegebenen Wortlaut:</p>
            <p className="italic">
              The use of information received from Google APIs will adhere to the Chrome Web
              Store User Data Policy, including the Limited Use requirements.
            </p>
          </Section>

          <p className="text-xs text-text-muted">
            {toSafeReactNode(t("privacyPage.dpaNoteBefore"))}
            <a href="mailto:privacy@takelead.app" className="text-accent hover:underline">
              privacy@takelead.app
            </a>
            {toSafeReactNode(t("privacyPage.dpaNoteAfter"))}
          </p>
        </div>

        <footer className="border-t border-border pt-6 text-center text-xs text-text-muted space-x-3">
          <Link to="/privacy" className="hover:text-text transition-colors">{toSafeReactNode(t("footer.privacy"))}</Link>
          <span className="text-border">·</span>
          <Link to="/terms" className="hover:text-text transition-colors">{toSafeReactNode(t("footer.terms"))}</Link>
          <span className="text-border">·</span>
          <Link to="/lawful-use" className="hover:text-text transition-colors">{toSafeReactNode(t("footer.lawfulUse"))}</Link>
          <span className="text-border">·</span>
          <Link to="/pricing" className="hover:text-text transition-colors">{toSafeReactNode(t("footer.pricing"))}</Link>
          <span className="text-border">·</span>
          <Link to="/lawful-use" className="hover:text-text transition-colors">Nutzungsrichtlinien</Link>
          <span className="text-border">·</span>
          <Link to="/impressum" className="hover:text-text transition-colors">Impressum</Link>
        </footer>
      </div>
    </div>
  );
}

export const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});
